import { useMemo } from "react";
import { ArrowRight, TrendingUp } from "lucide-react";
import { sourcePerformance } from "./reportHelpers";

/* Source Performance — Top 5 sources by lead count with mini bars.
 * Click a row to open Leads tab pre-filtered to that source.
 * Already-filtered upstream: archived/trashed/test leads are excluded. */
export default function SourcePerformanceCard({ leads, onPickSource }) {
  const rows = useMemo(() => sourcePerformance(leads, 5), [leads]);
  const total = useMemo(() => leads.length, [leads]);
  const max = rows[0]?.count || 1;

  return (
    <div
      className="rounded-2xl border border-[var(--swipe-accent)]/15 bg-white p-5"
      data-testid="source-performance-card"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-[16px] font-extrabold text-[var(--swipe-navy)] leading-tight">
              Source Performance
            </h3>
            <div className="text-[11.5px] text-slate-500">
              Top 5 sources · {total} active leads (excludes test, archived &amp; trash)
            </div>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-6 text-center text-[13px] text-slate-400 italic" data-testid="source-performance-empty">
          No active leads yet — sources will appear here as leads come in.
        </div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r, idx) => {
            const pct = Math.round((r.count / max) * 100);
            const share = total > 0 ? Math.round((r.count / total) * 100) : 0;
            return (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={() => onPickSource?.(r.key)}
                  data-testid={`source-perf-row-${idx}`}
                  className="w-full text-left group rounded-lg border border-slate-100 hover:border-[var(--swipe-accent)]/40 hover:bg-[var(--swipe-accent-soft)]/40 px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] text-[10.5px] font-bold shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-display font-bold text-[13.5px] text-[var(--swipe-navy)] truncate" title={r.key}>
                        {r.key}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11.5px] text-slate-500 tabular-nums">{share}%</span>
                      <span className="text-[14px] font-extrabold text-[var(--swipe-navy)] tabular-nums">{r.count}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[var(--swipe-accent)] transition-colors" />
                    </div>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[var(--swipe-accent)] transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
